import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Package, ChevronDown, ChevronRight, Calendar, Users as UsersIcon, Tag } from 'react-feather';
import { Button } from '../../components/ui/button';
import { FeatureTag } from '../../components/ui/feature-tag';
import { Badge } from '../../components/ui/badge';
import { FeatherArrowLeft, FeatherUsers, FeatherTrendingUp } from '../../components/ui/icons';
import DateSelector from '../../components/DailyReview/DateSelector';
import PickupStatsCards from '../../components/DailyReview/PickupStatsCards';
import TaskDistributionTable from '../../components/DailyReview/TaskDistributionTable';
import UnpickedTasksByResource from '../../components/DailyReview/UnpickedTasksByResource';
import BacklogSection from '../../components/DailyReview/BacklogSection';
import FeatureTagsSection from '../../components/DailyReview/FeatureTagsSection';
import CompletedTasksSection from '../../components/DailyReview/CompletedTasksSection';
import FeatureTagStats from '../../components/FeatureTags/FeatureTagStats';
import FeatureTagDetailModal from '../../components/FeatureTags/FeatureTagDetailModal';
import TaskDetailModal from '../../components/DailyReview/TaskDetailModal';
import NotesBottomBar from '../../components/DailyReview/NotesBottomBar';
import dailyReviewService from '../../services/dailyReviewService';
import api, { featureTagApi, releasesApi } from '../../services/api';
import { getProgressColor } from '../../utils/projectUtils';
import { getTodayLocalDate } from '../../utils/date';
import { isReleaseReadyForRelease } from '../../utils/releaseUtils';
import ReadyForReleaseBadge from '../../components/Releases/ReadyForReleaseBadge';
import './ProjectReviewDetail.css';

const ProjectReviewDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [date, setDate] = useState(() => {
        const dateParam = searchParams.get('date');
        return dateParam || getTodayLocalDate();
    });
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
    const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);
    const [allFeatureTags, setAllFeatureTags] = useState([]);
    const [allTasks, setAllTasks] = useState([]);
    const [releases, setReleases] = useState([]);
    const [expandedReleases, setExpandedReleases] = useState(new Set());
    const [reviewNotes, setReviewNotes] = useState({
        overallAssessment: '',
        issues: '',
        actionItems: '',
        resourceObservations: '',
        taskConcerns: '',
        featureTagNotes: '',
        additionalComments: '',
        goals: {
            tasks: [],
            features: [],
            personal: [],
            noGoals: false
        }
    });
    const [isNotesBarExpanded, setIsNotesBarExpanded] = useState(false);
    const saveTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        fetchSummaryData();
        fetchFeatureTagsAndTasks();
        fetchReleases();
    }, [projectId, date]);

    const fetchFeatureTagsAndTasks = async () => {
        try {
            // Fetch all feature tags for the project
            const featureTags = await featureTagApi.getByProject(projectId);
            setAllFeatureTags(featureTags || []);
            console.log('Fetched feature tags:', featureTags?.length || 0);

            // Fetch all tasks for the project
            const tasksResponse = await api.get(`/api/tasks/project/${projectId}`);
            setAllTasks(tasksResponse.data || []);
            console.log('Fetched tasks:', tasksResponse.data?.length || 0);
        } catch (err) {
            console.error('Error fetching feature tags and tasks:', err);
        }
    };

    const fetchReleases = async () => {
        try {
            const releasesData = await releasesApi.getProjectReleases(projectId);
            setReleases(releasesData || []);
        } catch (err) {
            console.error('Error fetching releases:', err);
        }
    };

    // Load notes from summaryData when available
    useEffect(() => {
        if (summaryData && summaryData.reviewNotes) {
            setReviewNotes(summaryData.reviewNotes);
        } else {
            // Initialize with empty notes if no review exists
            setReviewNotes({
                overallAssessment: '',
                issues: '',
                actionItems: '',
                resourceObservations: '',
                taskConcerns: '',
                featureTagNotes: '',
                additionalComments: '',
                goals: {
                    tasks: [],
                    features: [],
                    personal: [],
                    noGoals: false
                }
            });
        }
    }, [summaryData]);

    const fetchSummaryData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dailyReviewService.getProjectSummary(projectId, date);
            setSummaryData(data);
        } catch (err) {
            console.error('Error fetching project summary:', err);
            setError('Failed to load project summary. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
        // Update URL to reflect the new date
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('date', newDate);
        navigate(`/daily-work-plan-review/project/${projectId}?${newSearchParams.toString()}`, { replace: true });
    };

    // Auto-save notes with debounce
    const handleNotesChange = useCallback((updatedNotes) => {
        setReviewNotes(updatedNotes);
        
        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
        
        // Cancel in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new AbortController for this save
        abortControllerRef.current = new AbortController();
        const currentAbortController = abortControllerRef.current;
        
        // Set new timeout for auto-save (1.5 second debounce)
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                // Check if request was aborted before making the call
                if (currentAbortController.signal.aborted) {
                    return;
                }
                
                await dailyReviewService.saveNotes(projectId, updatedNotes, date, {
                    signal: currentAbortController.signal
                });
            } catch (error) {
                // Ignore abort errors and axios cancel errors
                if (error.name === 'AbortError' || 
                    error.name === 'CanceledError' || 
                    error.code === 'ERR_CANCELED' ||
                    (error.response && error.response.status === 0)) {
                    return;
                }
                console.error('Error auto-saving notes:', error);
                // Don't show error to user for auto-save failures
            } finally {
                // Clear the abort controller reference if this was the current request
                if (abortControllerRef.current === currentAbortController) {
                    abortControllerRef.current = null;
                }
            }
        }, 1500); // Increased to 1.5 seconds for better debouncing
    }, [projectId, date]);

    // Cleanup timeout and abort requests on unmount or when projectId/date changes
    useEffect(() => {
        return () => {
            // Clear timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
            }
            
            // Abort in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [projectId, date]);

    // Calculate feature completion and delay maps in a single pass (optimized)
    // Must be called before any early returns to comply with React Hooks rules
    const { featureCompletionMap, featureDelayMap } = useMemo(() => {
        const completionMap = {};
        const delayMap = {};
        
        allFeatureTags.forEach(tag => {
            const tagTasks = allTasks.filter(task => task.feature_tag?.id === tag.id);
            const totalTasks = tagTasks.length;
            const completedTasks = tagTasks.filter(task => 
                ['completed', 'released'].includes(task.status)
            );
            const completedCount = completedTasks.length;
            
            // Calculate completion percentage
            const completionPercentage = totalTasks > 0 
                ? Math.round((completedCount / totalTasks) * 100) 
                : 0;
            completionMap[tag.id] = completionPercentage;

            // Calculate delay days for completed features with due dates
            const allTasksCompleted = totalTasks > 0 && completedCount === totalTasks;
            
            if (allTasksCompleted && tag.due_date) {
                // Calculate last task update (completion date)
                const lastTaskUpdate = tagTasks.length > 0
                    ? tagTasks.reduce((latest, task) => {
                        if (!task.updated_at) return latest;
                        const taskDate = new Date(task.updated_at);
                        if (isNaN(taskDate.getTime())) return latest;
                        if (!latest) return taskDate;
                        return taskDate.getTime() > latest.getTime() ? taskDate : latest;
                    }, null)
                    : null;

                if (lastTaskUpdate) {
                    const dueDate = new Date(tag.due_date);
                    const completionDate = new Date(lastTaskUpdate);
                    const delayTime = completionDate - dueDate;
                    const delayDaysValue = Math.ceil(delayTime / (1000 * 60 * 60 * 24));
                    // Only set delay if completion was after due date
                    if (delayDaysValue > 0) {
                        delayMap[tag.id] = delayDaysValue;
                    }
                }
            }
        });
        
        return { featureCompletionMap: completionMap, featureDelayMap: delayMap };
    }, [allFeatureTags, allTasks]);

    // Calculate release feature tags with task counts
    const releaseFeatureTags = useMemo(() => {
        const featureTagsMap = {};
        
        releases.forEach(release => {
            const releaseFeatureTags = (release.release_feature_tags || [])
                .map(rft => rft.feature_tag)
                .filter(tag => tag); // Filter out null/undefined
            
            // Calculate task counts for each feature tag
            const featureTagsWithCounts = releaseFeatureTags.map(featureTag => {
                const tagTasks = allTasks.filter(task => task.feature_tag?.id === featureTag.id);
                const completedTasks = tagTasks.filter(task => 
                    ['completed', 'released'].includes(task.status)
                );
                const pendingTasks = tagTasks.filter(task => 
                    !['completed', 'released'].includes(task.status)
                );
                
                return {
                    ...featureTag,
                    completedCount: completedTasks.length,
                    pendingCount: pendingTasks.length,
                    totalCount: tagTasks.length
                };
            });
            
            featureTagsMap[release.id] = featureTagsWithCounts;
        });
        
        return featureTagsMap;
    }, [releases, allTasks]);

    // Calculate resource accountability per release
    const releaseResourceAccountability = useMemo(() => {
        const accountabilityMap = {};
        
        releases.forEach(release => {
            // Get all feature tag IDs for this release
            const releaseFeatureTagIds = (release.release_feature_tags || [])
                .map(rft => rft.feature_tag?.id)
                .filter(id => id);
            
            if (releaseFeatureTagIds.length === 0) {
                accountabilityMap[release.id] = [];
                return;
            }
            
            // Filter tasks to those in this release's feature tags
            const releaseTasks = allTasks.filter(task => 
                task.feature_tag?.id && releaseFeatureTagIds.includes(task.feature_tag.id)
            );
            
            // Filter to only open or in-progress tasks
            const activeTasks = releaseTasks.filter(task => 
                task.status === 'open' || task.status === 'in-progress'
            );
            
            // Group by assigned_to (resource)
            const resourceMap = new Map();
            const unassignedTasks = [];
            
            activeTasks.forEach(task => {
                if (!task.assigned_to) {
                    // Collect unassigned tasks
                    unassignedTasks.push(task);
                    return;
                }
                
                const userId = typeof task.assigned_to === 'object' 
                    ? task.assigned_to.id 
                    : task.assigned_to;
                
                if (!resourceMap.has(userId)) {
                    resourceMap.set(userId, {
                        userId,
                        name: typeof task.assigned_to === 'object'
                            ? (task.assigned_to.display_name || task.assigned_to.email || 'Unknown')
                            : 'Unknown',
                        email: typeof task.assigned_to === 'object'
                            ? task.assigned_to.email
                            : null,
                        taskCount: 0,
                        tasks: []
                    });
                }
                
                const resource = resourceMap.get(userId);
                resource.taskCount++;
                resource.tasks.push(task);
            });
            
            // Get sorted user groups
            const userGroups = Array.from(resourceMap.values())
                .sort((a, b) => b.taskCount - a.taskCount);
            
            // Add unassigned group at the end if there are unassigned tasks
            if (unassignedTasks.length > 0) {
                userGroups.push({
                    userId: 'unassigned',
                    name: 'Unassigned',
                    email: null,
                    taskCount: unassignedTasks.length,
                    tasks: unassignedTasks
                });
            }
            
            accountabilityMap[release.id] = userGroups;
        });
        
        return accountabilityMap;
    }, [releases, allTasks]);

    // Handle release expansion toggle
    const handleReleaseExpand = (releaseId) => {
        setExpandedReleases(prev => {
            const newSet = new Set(prev);
            if (newSet.has(releaseId)) {
                newSet.delete(releaseId);
            } else {
                newSet.add(releaseId);
            }
            return newSet;
        });
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#059669';
            case 'in_progress': return '#2563eb';
            case 'planned': return '#4b5563';
            case 'cancelled': return '#dc2626';
            default: return '#4b5563';
        }
    };

    if (loading) {
        return (
            <div className="project-review-detail-container">
                <div className="project-review-detail-loading">
                    <div className="loading-spinner" />
                    <p>Loading project summary...</p>
                </div>
            </div>
        );
    }

    if (error || !summaryData) {
        return (
            <div className="project-review-detail-container">
                <div className="project-review-detail-error">
                    <p>{error || 'Project not found'}</p>
                    <Button onClick={fetchSummaryData}>Retry</Button>
                </div>
            </div>
        );
    }

    const { project, pickupStats, taskDistribution, unpickedTasksByResource, backlog, featureTags, completedTasks } = summaryData;

    return (
        <div className={`project-review-detail-container ${isNotesBarExpanded ? 'notes-bar-expanded' : ''}`}>
            <div className="project-review-detail-header">
                <div className="project-review-detail-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate(`/daily-work-plan-review?date=${date}`)}
                        title="Back to Projects"
                    />
                    <div>
                        <h1 className="project-review-detail-title">{project.name}</h1>
                        {project.abbreviation && (
                            <p className="project-review-detail-subtitle">{project.abbreviation}</p>
                        )}
                    </div>
                </div>
                <div className="project-review-detail-header-actions">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherTrendingUp />}
                        onClick={() => navigate(`/daily-work-plan-review/project/${projectId}/today-outcome?date=${date}`)}
                    >
                        Today's Outcome
                    </Button>
                    <Button
                        variant="review"
                        icon={<FeatherUsers />}
                        onClick={() => navigate(`/daily-work-plan-review/project/${projectId}/resource-review?date=${date}`)}
                    >
                        Start Resource Review
                    </Button>
                    <DateSelector date={date} onDateChange={handleDateChange} />
                </div>
            </div>

            <div className="project-review-detail-content-sharp">
                <PickupStatsCards pickupStats={pickupStats} backlogCount={backlog?.length || 0} />
                <div className="feature-tag-stats-wrapper">
                    <FeatureTagStats 
                        featureTags={allFeatureTags}
                        tasks={allTasks}
                        defaultExpanded={false}
                        date={date}
                        taskDistribution={taskDistribution || []}
                    />
                </div>

                {/* Release Stats Section */}
                {releases.length > 0 && (
                    <div className="release-stats-section">
                        <div className="release-stats-section-header">
                            <div className="release-stats-section-title">
                                <Package size={18} />
                                <h2>Release Management</h2>
                                <span className="release-stats-section-count">({releases.length})</span>
                            </div>
                        </div>
                        <div className="release-stats-list">
                            {releases.map(release => {
                                const isExpanded = expandedReleases.has(release.id);
                                const progress = release.insights?.completion_percentage || 0;
                                const taskCount = release.insights?.total_tasks || 0;
                                const completedTaskCount = release.insights?.completed_tasks || 0;
                                const resourceAccountability = releaseResourceAccountability[release.id] || [];
                                
                                return (
                                    <div key={release.id} className="release-stats-card">
                                        <div 
                                            className="release-stats-card-header"
                                            onClick={() => handleReleaseExpand(release.id)}
                                        >
                                            <div className="release-stats-card-title-section">
                                                <button className="release-stats-expand-button">
                                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                </button>
                                                <div className="release-stats-card-title">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                        <span className="release-stats-name">{release.name}</span>
                                                        {isReleaseReadyForRelease(release, allTasks) && <ReadyForReleaseBadge />}
                                                    </div>
                                                    <span className="release-stats-version">v{release.version}</span>
                                                </div>
                                            </div>
                                            <div className="release-stats-card-meta">
                                                <span 
                                                    className="release-stats-status-badge"
                                                    style={{ 
                                                        backgroundColor: getStatusColor(release.status) + '20',
                                                        color: getStatusColor(release.status)
                                                    }}
                                                >
                                                    {release.status}
                                                </span>
                                                {release.target_date && (
                                                    <div className="release-stats-date">
                                                        <Calendar size={14} />
                                                        <span>{formatDate(release.target_date)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {isExpanded && (
                                            <div className="release-stats-card-content">
                                                {/* Release Progress */}
                                                {progress > 0 && (
                                                    <div className="release-stats-progress-section">
                                                        <div className="release-stats-progress-header">
                                                            <span className="release-stats-progress-label">Overall Progress</span>
                                                            <span className="release-stats-progress-percentage">{progress}%</span>
                                                        </div>
                                                        <div className="release-stats-progress-bar">
                                                            <div 
                                                                className="release-stats-progress-fill"
                                                                style={{ 
                                                                    width: `${progress}%`,
                                                                    backgroundColor: getProgressColor(progress)
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="release-stats-progress-details">
                                                            <span>{completedTaskCount} of {taskCount} tasks completed</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Feature Tags */}
                                                {(() => {
                                                    const featureTags = releaseFeatureTags[release.id] || [];
                                                    return featureTags.length > 0 ? (
                                                        <div className="release-stats-feature-tags">
                                                            <div className="release-stats-feature-tags-header">
                                                                <Tag size={16} />
                                                                <span>Feature Tags</span>
                                                                <span className="release-stats-feature-tags-count">
                                                                    ({featureTags.length} {featureTags.length === 1 ? 'tag' : 'tags'})
                                                                </span>
                                                            </div>
                                                            <div className="release-stats-feature-tags-list">
                                                                {featureTags.map(featureTag => {
                                                                    const fullFeatureTag = allFeatureTags.find(ft => ft.id === featureTag.id);
                                                                    return (
                                                                        <div 
                                                                            key={featureTag.id}
                                                                            className="release-feature-tag-with-counts"
                                                                            title={`${featureTag.completedCount} completed, ${featureTag.pendingCount} pending`}
                                                                        >
                                                                            <div
                                                                                className="clickable-feature-tag"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (fullFeatureTag) {
                                                                                        setSelectedFeatureTag(fullFeatureTag);
                                                                                        setIsFeatureTagModalOpen(true);
                                                                                    }
                                                                                }}
                                                                                style={{ cursor: fullFeatureTag ? 'pointer' : 'default' }}
                                                                                title={fullFeatureTag ? "Click to view feature details" : undefined}
                                                                            >
                                                                                <FeatureTag
                                                                                    name={featureTag.name}
                                                                                    color={featureTag.color || '#6366f1'}
                                                                                    size="small"
                                                                                />
                                                                            </div>
                                                                            <span className="feature-tag-counts">
                                                                                <span className="count-completed">{featureTag.completedCount}</span>
                                                                                <span className="count-separator">/</span>
                                                                                <span className="count-pending">{featureTag.pendingCount}</span>
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })()}

                                                {/* Resource Accountability */}
                                                {resourceAccountability.length > 0 ? (
                                                    <div className="release-stats-resource-accountability">
                                                        <div className="release-stats-resource-header">
                                                            <UsersIcon size={16} />
                                                            <span>Resource Accountability</span>
                                                            <span className="release-stats-resource-count">
                                                                ({resourceAccountability.length} {resourceAccountability.length === 1 ? 'resource' : 'resources'})
                                                            </span>
                                                        </div>
                                                        <div className="release-stats-resource-list">
                                                            {resourceAccountability.map(resource => (
                                                                <div key={resource.userId} className="release-stats-resource-item">
                                                                    <div className="release-stats-resource-info">
                                                                        <span className="release-stats-resource-name">{resource.name}</span>
                                                                        <span className="release-stats-resource-task-count">
                                                                            {resource.taskCount} {resource.taskCount === 1 ? 'task' : 'tasks'} (open/in-progress)
                                                                        </span>
                                                                    </div>
                                                                    {resource.tasks && resource.tasks.length > 0 && (() => {
                                                                        // Group tasks by feature tag
                                                                        const tasksByFeature = {};
                                                                        const ungroupedTasks = [];
                                                                        
                                                                        resource.tasks.forEach(task => {
                                                                            if (task.feature_tag && task.feature_tag.id) {
                                                                                const featureId = task.feature_tag.id;
                                                                                if (!tasksByFeature[featureId]) {
                                                                                    tasksByFeature[featureId] = {
                                                                                        featureTag: task.feature_tag,
                                                                                        tasks: []
                                                                                    };
                                                                                }
                                                                                tasksByFeature[featureId].tasks.push(task);
                                                                            } else {
                                                                                ungroupedTasks.push(task);
                                                                            }
                                                                        });
                                                                        
                                                                        const featureGroups = Object.values(tasksByFeature);
                                                                        
                                                                        return (
                                                                            <div className="release-stats-resource-tasks">
                                                                                {featureGroups.map(group => (
                                                                                    <div key={group.featureTag.id} className="release-stats-feature-group">
                                                                                        <div className="release-stats-feature-group-header">
                                                                                            <div
                                                                                                className="clickable-feature-tag"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    const fullFeatureTag = allFeatureTags.find(ft => ft.id === group.featureTag.id);
                                                                                                    if (fullFeatureTag) {
                                                                                                        setSelectedFeatureTag(fullFeatureTag);
                                                                                                        setIsFeatureTagModalOpen(true);
                                                                                                    }
                                                                                                }}
                                                                                                style={{ cursor: allFeatureTags.find(ft => ft.id === group.featureTag.id) ? 'pointer' : 'default' }}
                                                                                                title={allFeatureTags.find(ft => ft.id === group.featureTag.id) ? "Click to view feature details" : undefined}
                                                                                            >
                                                                                                <FeatureTag
                                                                                                    name={group.featureTag.name}
                                                                                                    color={group.featureTag.color || '#6366f1'}
                                                                                                    size="small"
                                                                                                />
                                                                                            </div>
                                                                                            <span className="release-stats-feature-group-count">
                                                                                                {group.tasks.length} {group.tasks.length === 1 ? 'task' : 'tasks'}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="release-stats-feature-group-tasks">
                                                                                            {group.tasks.map(task => (
                                                                                                <div 
                                                                                                    key={task.id} 
                                                                                                    className="release-stats-resource-task-item"
                                                                                                    onClick={() => setSelectedTask({
                                                                                                        id: task.id,
                                                                                                        title: task.title,
                                                                                                        display_id: task.display_id,
                                                                                                        description: task.description,
                                                                                                        status: task.status,
                                                                                                        feature_tag: task.feature_tag,
                                                                                                        created_at: task.created_at,
                                                                                                        updated_at: task.updated_at
                                                                                                    })}
                                                                                                >
                                                                                                    <div className="release-stats-task-content">
                                                                                                        <div className="release-stats-task-title-row">
                                                                                                            <span className="release-stats-task-title">
                                                                                                                {task.display_id && (
                                                                                                                    <span className="release-stats-task-display-id">[{task.display_id}]</span>
                                                                                                                )}
                                                                                                                {task.title}
                                                                                                            </span>
                                                                                                            <Badge
                                                                                                                variant={task.status === 'in-progress' ? 'warning' : 'secondary'}
                                                                                                            >
                                                                                                                {task.status}
                                                                                                            </Badge>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                                {ungroupedTasks.length > 0 && (
                                                                                    <div className="release-stats-feature-group">
                                                                                        <div className="release-stats-feature-group-header">
                                                                                            <span className="release-stats-feature-group-label">Other Tasks</span>
                                                                                            <span className="release-stats-feature-group-count">
                                                                                                {ungroupedTasks.length} {ungroupedTasks.length === 1 ? 'task' : 'tasks'}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="release-stats-feature-group-tasks">
                                                                                            {ungroupedTasks.map(task => (
                                                                                                <div 
                                                                                                    key={task.id} 
                                                                                                    className="release-stats-resource-task-item"
                                                                                                    onClick={() => setSelectedTask({
                                                                                                        id: task.id,
                                                                                                        title: task.title,
                                                                                                        display_id: task.display_id,
                                                                                                        description: task.description,
                                                                                                        status: task.status,
                                                                                                        feature_tag: task.feature_tag,
                                                                                                        created_at: task.created_at,
                                                                                                        updated_at: task.updated_at
                                                                                                    })}
                                                                                                >
                                                                                                    <div className="release-stats-task-content">
                                                                                                        <div className="release-stats-task-title-row">
                                                                                                            <span className="release-stats-task-title">
                                                                                                                {task.display_id && (
                                                                                                                    <span className="release-stats-task-display-id">[{task.display_id}]</span>
                                                                                                                )}
                                                                                                                {task.title}
                                                                                                            </span>
                                                                                                            <Badge
                                                                                                                variant={task.status === 'in-progress' ? 'warning' : 'secondary'}
                                                                                                            >
                                                                                                                {task.status}
                                                                                                            </Badge>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="release-stats-no-resources">
                                                        <span>No active tasks assigned to resources for this release</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <TaskDistributionTable 
                    taskDistribution={taskDistribution} 
                    onTaskClick={setSelectedTask}
                    featureCompletionMap={featureCompletionMap}
                    allFeatureTags={allFeatureTags}
                    allTasks={allTasks}
                />
                <UnpickedTasksByResource 
                    unpickedTasksByResource={unpickedTasksByResource}
                    projectId={projectId}
                    onTaskClick={setSelectedTask}
                    allFeatureTags={allFeatureTags}
                    allTasks={allTasks}
                />
                <BacklogSection 
                    backlog={backlog} 
                    projectId={projectId}
                    onTaskClick={setSelectedTask}
                    allFeatureTags={allFeatureTags}
                    allTasks={allTasks}
                />
                <FeatureTagsSection 
                    featureTags={featureTags} 
                    projectId={projectId}
                    onTaskClick={setSelectedTask}
                    featureCompletionMap={featureCompletionMap}
                    featureDelayMap={featureDelayMap}
                    allFeatureTags={allFeatureTags}
                    allTasks={allTasks}
                />
                <CompletedTasksSection 
                    completedTasks={completedTasks} 
                    projectId={projectId}
                    onTaskClick={setSelectedTask}
                />
            </div>

            {/* Task Detail Modal */}
            <TaskDetailModal
                task={selectedTask}
                isOpen={!!selectedTask}
                onClose={() => setSelectedTask(null)}
            />

            {/* Notes Bottom Bar */}
            <NotesBottomBar
                notes={reviewNotes}
                onNotesChange={handleNotesChange}
                onExpandedChange={setIsNotesBarExpanded}
                projectId={projectId}
                date={date}
            />

            {/* Feature Tag Detail Modal */}
            {selectedFeatureTag && (
                <FeatureTagDetailModal
                    tag={selectedFeatureTag}
                    isOpen={isFeatureTagModalOpen}
                    onClose={() => {
                        setIsFeatureTagModalOpen(false);
                        setSelectedFeatureTag(null);
                    }}
                    tasks={allTasks.filter(task => task.feature_tag?.id === selectedFeatureTag.id)}
                    users={[]}
                />
            )}
        </div>
    );
};

export default ProjectReviewDetail;
